import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import MyOrdersProductPositions from './modal_windows/MyOrdersProductPositions';

import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState,
  Pagination, BulkBar,
} from '../../ui/CrmPage';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const Production = () => {
  const [orders, setOrders] = useState([]);
  const [, setStatuses] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [fallbackHeader, setFallbackHeader] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const ordersPerPage = 15;

  useEffect(() => {
    fetchOrders();
    fetchStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const fetchOrders = () => {
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/production_view`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        const grouped = groupOrders(list);
        if (grouped.length === 0) setOrders([]);
        else updateOrdersWithPaymentStatus(grouped);
      })
      .catch(err => {
        console.error('Ошибка загрузки production_view:', err);
        setOrders([]);
      })
      .finally(() => setIsLoading(false));
  };

  const fetchStatuses = () => {
    fetch(`${process.env.REACT_APP_API_URL}v1/statuses`)
      .then(res => res.json())
      .then(data => {
        const map = data.reduce((acc, s) => { acc[s.name] = s.id; return acc; }, {});
        setStatuses(map);
      })
      .catch(err => console.error('Ошибка загрузки статусов:', err));
  };

  const groupOrders = (list) => {
    const grouped = list.reduce((acc, order) => {
      if (!acc[order.id_orders]) acc[order.id_orders] = [];
      acc[order.id_orders].push(order);
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const flatFindOrder = (orderId) => orders.flat().find(o => o.id_orders === orderId);

  const updateOrderStatus = async (orderId, newStatus, override = {}) => {
    const flatOrder = flatFindOrder(orderId);
    if (!flatOrder) throw new Error('Заказ не найден');
    const updatedData = {
      delivery_address: flatOrder.delivery_address,
      delivery_date: flatOrder.delivery_date,
      id_status: newStatus,
      id_payment_status: flatOrder.id_payment_status,
      total_amount: flatOrder.total_amount,
      id_organization: flatOrder.id_organization,
      id_division: flatOrder.id_division,
      id_drivers: flatOrder.id_drivers || null,
      week_number: flatOrder.week_number,
      ...override,
    };
    const response = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/${orderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Ошибка обновления заказа');
    }
  };

  const handleBulkUpdate = async (status) => {
    setIsLoading(true);
    try {
      await Promise.all(selectedOrders.map(id => updateOrderStatus(id, status)));
      setSelectedOrders([]);
      fetchOrders();
    } catch (err) {
      alert(`Ошибка: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentStatus = async (orderId, statusName) => {
    try {
      const route = statusName === 'Отменен пользователем' ? `v1/tickets/${orderId}` : `v1/orders/${orderId}`;
      const res = await fetch(`${process.env.REACT_APP_API_URL}${route}`);
      if (!res.ok) return 'Неизвестен';
      const data = await res.json();
      const s = data.id_payment_status;
      return s === 1 ? 'Оплачен' : s === 2 ? 'Ожидает оплаты' : 'Неизвестен';
    } catch { return 'Неизвестен'; }
  };

  const updateOrdersWithPaymentStatus = async (groups) => {
    const updated = await Promise.all(groups.map(async (group) => {
      const order = group[0];
      const paymentStatus = await fetchPaymentStatus(order.id_orders, order.status_name);
      return group.map(item => ({ ...item, payment_status: paymentStatus }));
    }));
    setOrders(updated);
  };

  // --- summary
  const summary = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter(g => /ожидает|подтвержд/i.test(g[0].status_name || '')).length;
    const inWork = orders.filter(g => /собира|производ/i.test(g[0].status_name || '')).length;
    const ready = orders.filter(g => /укомплект|готов/i.test(g[0].status_name || '')).length;
    return { total, pending, inWork, ready };
  }, [orders]);

  const regionOptions = useMemo(() => {
    const set = new Set();
    orders.forEach((g) => {
      const r = g[0]?.region_name;
      if (r) set.add(r);
    });
    return Array.from(set).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(group => {
      const o = group[0];
      if (regionFilter !== 'all' && (o.region_name || '') !== regionFilter) return false;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [orders, searchTerm, regionFilter]);

  const sortedOrders = useMemo(() =>
    [...filteredOrders].sort((a, b) => {
      if (a[0].status_name === 'Ожидает подтверждения') return -1;
      if (b[0].status_name === 'Ожидает подтверждения') return 1;
      return 0;
    }), [filteredOrders]);

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  const handleRowClick = (orderGroup) => {
    const row = orderGroup[0];
    setSelectedOrderId(row.id_orders);
    setFallbackHeader({
      order_number: row.order_number,
      order_date: row.order_date,
      delivery_date: row.delivery_date,
      status_name: row.status_name,
      delivery_address: row.delivery_address,
      client_name: row.client_name,
    });
    setIsDetailsOpen(true);
  };

  return (
    <div className="my-orders-container crmpage">
      <div className="my-orders-sidebar">
        <Sidebar />
      </div>

      <div className="my-orders-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Производство</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"        value={summary.total}   accent="neutral" />
          <StatCard label="Ожидают"      value={summary.pending} accent="warning" />
          <StatCard label="В работе"     value={summary.inWork}  accent="info" />
          <StatCard label="Готовы"       value={summary.ready}   accent="success" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес…"
          />
          <div className="crmpage__pills">
            <FilterPill
              active={regionFilter === 'all'}
              onClick={() => { setRegionFilter('all'); setCurrentPage(1); }}
              count={orders.length}
            >Все регионы</FilterPill>
            {regionOptions.map((r) => (
              <FilterPill
                key={r}
                active={regionFilter === r}
                onClick={() => { setRegionFilter(r); setCurrentPage(1); }}
                count={orders.filter((g) => (g[0].region_name || '') === r).length}
              >{r}</FilterPill>
            ))}
          </div>
        </section>

        <TableCard>
          {isLoading && orders.length === 0 ? (
            <LoadingState text="Загружаем производство…" />
          ) : currentOrders.length === 0 ? (
            <EmptyState
              title="Заказов нет"
              text="Здесь появятся заказы, ожидающие производства."
            />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="crmpage__th-check"></th>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1000">Доставка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((group) => {
                      const order = group[0];
                      const checked = selectedOrders.includes(order.id_orders);
                      return (
                        <tr
                          key={order.id_orders}
                          className={checked ? 'crmpage__row crmpage__row--selected' : 'crmpage__row'}
                          onClick={() => handleRowClick(group)}
                        >
                          <td className="crmpage__td-check" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="crmpage__check"
                              checked={checked}
                              onChange={() => handleCheckboxChange(order.id_orders)}
                              disabled={isLoading}
                            />
                          </td>
                          <td><span className="crmpage__num">№{order.order_number}</span></td>
                          <td>
                            <div className="crmpage__primary">{order.client_name || '—'}</div>
                            <div className="crmpage__sub">{order.region_name}</div>
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">{order.delivery_address || '—'}</td>
                          <td><StatusBadge name={order.status_name} /></td>
                          <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="crmpage__cards">
                {currentOrders.map((group) => {
                  const o = group[0];
                  const checked = selectedOrders.includes(o.id_orders);
                  return (
                    <div
                      key={o.id_orders}
                      className={`crmpage__card ${checked ? 'is-selected' : ''}`}
                      onClick={() => handleRowClick(group)}
                    >
                      <div className="crmpage__card-head">
                        <label className="crmpage__card-check" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="crmpage__check"
                            checked={checked} onChange={() => handleCheckboxChange(o.id_orders)} />
                          <span className="crmpage__num">№{o.order_number}</span>
                        </label>
                        <StatusBadge name={o.status_name} />
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Клиент</span>
                        <span className="crmpage__card-val">{o.client_name || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Адрес</span>
                        <span className="crmpage__card-val">{o.delivery_address || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Доставка</span>
                        <span className="crmpage__card-val">{formatDate(o.delivery_date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>

        <BulkBar count={selectedOrders.length} onClear={() => setSelectedOrders([])}>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => handleBulkUpdate(7)} disabled={isLoading}>
            <BuildOutlinedIcon sx={{ fontSize: 18 }} /> Собирать
          </button>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => handleBulkUpdate(8)} disabled={isLoading}>
            <DoneAllIcon sx={{ fontSize: 18 }} /> Укомплектован
          </button>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => {
            if (window.confirm('Вернуть на доработку?')) handleBulkUpdate(6);
          }} disabled={isLoading}>
            <ReplayOutlinedIcon sx={{ fontSize: 18 }} /> На доработку
          </button>
          <button className="crmpage__btn crmpage__btn--primary" onClick={() => handleBulkUpdate(5)} disabled={isLoading}>
            <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} /> Готово
          </button>
        </BulkBar>

        {isDetailsOpen && (
          <MyOrdersProductPositions
            isOpen={isDetailsOpen}
            onClose={() => setIsDetailsOpen(false)}
            orderId={selectedOrderId}
            fallbackHeader={fallbackHeader || {}}
          />
        )}
      </div>
    </div>
  );
};

export default Production;
